// Platform growth analytics (Platform Operations Console P3 — advanced analytics).
// Real time-series: tenant + user signups by month (auth /platform/analytics/growth)
// and revenue by month (billing). Charts via recharts.
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Button, Space, Typography, message, Empty } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
  ResponsiveContainer, ComposedChart, AreaChart, BarChart, Area, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { PageHeader } from '../../components/console';
import { getPlatformGrowth, type PlatformGrowth } from '../../services/platformAnalyticsService';
import { getRevenue } from '../../services/billingService';

const { Text } = Typography;

// KaziPeople-consistent series colours (recharts needs concrete strings, not CSS vars).
const ORANGE = '#F28A3A';
const GREEN = '#16A34A';
const BLUE = '#2563EB';

interface RevenuePoint { month: string; amount: number | string; count?: number }

const grid = <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #eee)" />;

export const GrowthAnalyticsPage: React.FC = () => {
  const [growth, setGrowth] = useState<PlatformGrowth | null>(null);
  const [revenue, setRevenue] = useState<RevenuePoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true); else setRefreshing(true);
    try {
      const [g, rev] = await Promise.all([
        getPlatformGrowth(),
        getRevenue().catch(() => []), // billing optional — don't fail the page
      ]);
      setGrowth(g);
      setRevenue(Array.isArray(rev) ? rev.map((r: any) => ({ ...r, amount: Number(r.amount || 0) })) : []);
    } catch {
      if (initial) message.error('Could not load growth analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  useEffect(() => { void load(true); }, [load]);

  const tenants = growth?.tenantsByMonth ?? [];
  const users = growth?.usersByMonth ?? [];
  const totalTenants = tenants.length ? tenants[tenants.length - 1].cumulative : 0;
  const totalUsers = users.length ? users[users.length - 1].cumulative : 0;
  const last3 = tenants.slice(-3).reduce((s, p) => s + p.count, 0);

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Growth analytics"
        subtitle="Tenant & user growth and revenue trends across the platform"
        actions={
          <Button icon={<ReloadOutlined spin={refreshing} />} onClick={() => void load(false)} disabled={refreshing}>
            Refresh
          </Button>
        }
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={8}><Card loading={loading}><Statistic title="Total tenants" value={totalTenants} /></Card></Col>
        <Col xs={12} md={8}><Card loading={loading}><Statistic title="Total users" value={totalUsers} /></Card></Col>
        <Col xs={12} md={8}><Card loading={loading}><Statistic title="New tenants (last 3 mo)" value={last3} /></Card></Col>
      </Row>

      <Card title="Tenant growth" style={{ marginBottom: 16 }} loading={loading}>
        {tenants.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={tenants} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              {grid}
              <XAxis dataKey="month" fontSize={12} />
              <YAxis yAxisId="l" fontSize={12} allowDecimals={false} />
              <YAxis yAxisId="r" orientation="right" fontSize={12} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="l" dataKey="count" name="New tenants" fill={ORANGE} radius={[3, 3, 0, 0]} />
              <Line yAxisId="r" type="monotone" dataKey="cumulative" name="Total tenants" stroke={GREEN} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="User growth (cumulative)" style={{ marginBottom: 16 }} loading={loading}>
        {users.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={users} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              {grid}
              <XAxis dataKey="month" fontSize={12} />
              <YAxis fontSize={12} allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="cumulative" name="Total users" stroke={BLUE} fill={BLUE} fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="Revenue by month" loading={loading}>
        {revenue.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No revenue data" />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={revenue} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              {grid}
              <XAxis dataKey="month" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip formatter={(v: any) => Number(v).toLocaleString()} />
              <Bar dataKey="amount" name="Revenue" fill={ORANGE} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
        Tenant/user counts bucketed by signup month; revenue from recorded payments per month.
      </Text>
    </div>
  );
};

export default GrowthAnalyticsPage;
